/**
 * ISEC (Indication / Effectiveness / Safety / Compliance) counselling question sets.
 *
 * Single source of truth for the per-letter talking points a pharmacist works
 * through on the Clinical Verification sign page. The SAME set is rendered in
 * the Counseling queue so a topic is documented once (GP-928). Do not copy
 * these lists into another component — import from here (see CLAUDE.md: one
 * source of truth for shared data).
 *
 * Question definitions supplied by John Park (Pharmacy Manager) to replace the
 * separate CV 7-topic list and the 13-item Kroll counselling checklist.
 * July 3 UAT feedback (OCP round 2): every question is a documentable point —
 * checkbox + free-form note, identical on both surfaces — so each question
 * carries a stable `id` that keys the persisted per-point state.
 */

export type IsecKey = 'indication' | 'effectiveness' | 'safety' | 'compliance';

export interface IsecQuestion {
  /** Stable persisted key for this point — never rename once shipped. */
  id: string;
  text: string;
}

export interface IsecQuestionGroup {
  key: IsecKey;
  letter: string;
  title: string;
  questions: IsecQuestion[];
}

export const ISEC_QUESTIONS: IsecQuestionGroup[] = [
  {
    key: 'indication',
    letter: 'I',
    title: 'Indication',
    questions: [
      { id: 'ind-indication', text: 'Indication' },
      { id: 'ind-taken-before', text: 'Have you taken this medication before?' },
      { id: 'ind-purpose', text: 'Purpose of therapy' },
      { id: 'ind-other-conditions', text: 'Effect on other conditions (if applicable)' },
    ],
  },
  {
    key: 'effectiveness',
    letter: 'E',
    title: 'Effectiveness',
    questions: [
      { id: 'eff-benefits', text: 'Expected benefits / goals of therapy' },
      { id: 'eff-signs-working', text: 'Signs medication is working' },
      { id: 'eff-onset', text: 'Onset of action' },
      { id: 'eff-self-monitoring', text: 'Self-monitoring techniques' },
      { id: 'eff-how-to-take', text: 'How to take / use (dose, route, technique)' },
      { id: 'eff-schedule-duration', text: 'Dosing schedule & duration' },
    ],
  },
  {
    key: 'safety',
    letter: 'S',
    title: 'Safety',
    questions: [
      { id: 'saf-admin-instructions', text: 'Special administration instructions' },
      { id: 'saf-side-effects', text: 'Potential side effects' },
      { id: 'saf-interactions', text: 'Interactions (Rx, OTC, food, alcohol)' },
      { id: 'saf-contraindications', text: 'Contraindications / precautions (if applicable)' },
      { id: 'saf-seek-medical', text: 'When to seek medical attention' },
      { id: 'saf-missed-dose', text: 'What to do if a dose is missed' },
      { id: 'saf-storage', text: 'Proper storage' },
      { id: 'saf-injection-technique', text: 'Injection technique / site rotation (if applicable)' },
    ],
  },
  {
    key: 'compliance',
    letter: 'C',
    title: 'Compliance',
    questions: [
      { id: 'com-adherence', text: 'Adherence importance & strategies' },
      { id: 'com-refill-process', text: 'Refill process & timing' },
      { id: 'com-missed-dose', text: 'Missed dose' },
    ],
  },
];

/**
 * Letter badge colors shared by the CV sign page and the Counseling dialog so
 * the ISEC letters look identical on both surfaces.
 */
export const ISEC_LETTER_COLORS: Record<IsecKey, string> = {
  indication: '#1565c0',
  effectiveness: '#2e7d32',
  safety: '#e65100',
  compliance: '#546e7a',
};

/** Lookup helper: questions keyed by ISEC letter key. */
export const ISEC_QUESTIONS_BY_KEY: Record<IsecKey, IsecQuestion[]> = ISEC_QUESTIONS.reduce(
  (acc, group) => {
    acc[group.key] = group.questions;
    return acc;
  },
  {} as Record<IsecKey, IsecQuestion[]>,
);

/** Display label per question id — used by history/read-back surfaces. */
export const ISEC_POINT_LABELS: Record<string, string> = ISEC_QUESTIONS.reduce(
  (acc, group) => {
    group.questions.forEach((q) => {
      acc[q.id] = q.text;
    });
    return acc;
  },
  {} as Record<string, string>,
);

/** Per-point documentation state, keyed by question id. */
export interface IsecPointItem {
  checked: boolean;
  notes: string;
}
export type IsecPoints = Record<string, IsecPointItem>;

/** All question ids, in display order. */
export const ISEC_POINT_IDS: string[] = ISEC_QUESTIONS.flatMap((g) =>
  g.questions.map((q) => q.id),
);

export const EMPTY_ISEC_POINTS: IsecPoints = Object.fromEntries(
  ISEC_POINT_IDS.map((id) => [id, { checked: false, notes: '' }]),
);

/** Reserved clinicalNotes key for the free-text counselling summary (the clinical note). */
export const ISEC_SUMMARY_KEY = 'summary';

/** Display label for the summary field/notes — same wording on every surface. */
export const ISEC_SUMMARY_LABEL = 'Summary / Counselling Note';
